<template>
  <div class="contrat-step-wizard">
    <div class="step-header">
      <h3>Créer le contrat de location</h3>
      <p>Saisissez les informations du contrat (seuls les champs supportés par l'API)</p>
    </div>

    <!-- Selected Information Summary -->
    <div class="selection-summary">
      <div class="summary-grid">
        <div class="summary-card client-summary">
          <h4>Client sélectionné</h4>
          <div class="info">
            <strong>{{ client.name || client.nom_complet || client.raison_sociale }}</strong>
            <span>ID: {{ client.id }}</span>
            <span>{{ (client.tenant_type || client.type) === 'physique' ? 'Physique' : 'Moral' }}</span>
            <span>{{ client.identification || client.cin || client.registre_commerce }}</span>
          </div>
        </div>
        <div class="summary-card bien-summary">
          <h4>Bien sélectionné</h4>
          <div class="info">
            <strong>{{ bien.address || bien.adresse }}</strong>
            <span>ID: {{ bien.id }}</span>
            <span>{{ getTypeLabel(bien.type) }}</span>
            <span>{{ formatPrice(bien.price || bien.prix_location) }} DH/mois</span>
            <span>{{ bien.surface ? bien.surface + ' m²' : '' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Contract Form - BACKEND COMPATIBLE FIELDS ONLY -->
    <form @submit.prevent="createContract" class="contrat-form">
      <div class="form-section">
        <h4>Période de location</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Date de début *</label>
            <input 
              type="date" 
              v-model="contratData.start_date" 
              required
              @change="calculateDuration"
            />
          </div>
          <div class="form-group">
            <label>Date de fin *</label>
            <input 
              type="date" 
              v-model="contratData.end_date" 
              required
              @change="calculateDuration"
            />
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Durée (calculée automatiquement)</label>
            <div class="calculated-field">
              <input 
                type="text" 
                :value="formattedDuration"
                readonly
                placeholder="Entrez les dates pour calculer"
              />
              <span class="calc-icon">📊</span>
            </div>
            <small class="form-help">La durée est calculée automatiquement en fonction des dates</small>
          </div>
          <div class="form-group">
            <label>Date du premier paiement</label>
            <input 
              type="date" 
              v-model="contratData.first_payment_date"
              :min="contratData.start_date"
            />
            <small class="form-help">Optionnel - utilisé pour calculer les échéances de paiement</small>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Conditions financières</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Montant du loyer (DH) *</label>
            <input 
              type="number" 
              v-model="contratData.rent_amount" 
              required
              min="0"
              step="0.01"
              :placeholder="bien.price || bien.prix_location"
            />
          </div>
          <div class="form-group">
            <label>Fréquence de paiement *</label>
            <select v-model="contratData.payment_frequency" required>
              <option value="monthly">Mensuel</option>
              <option value="quarterly">Trimestriel</option>
              <option value="semestriel">Semestriel</option>
              <option value="annuel">Annuel</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group">
            <label>Taux de taxe (%) *</label>
            <input 
              type="number" 
              v-model="contratData.tax_rate" 
              required
              min="0"
              max="100"
              step="0.01"
              placeholder="20.00"
            />
          </div>
          <div class="form-group">
            <label>Remise (%)</label>
            <input 
              type="number" 
              v-model="contratData.discount" 
              min="0"
              max="100"
              step="0.01"
              placeholder="0.00"
            />
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Statut et notes</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Statut *</label>
            <select v-model="contratData.status" required>
              <option value="pending">En attente</option>
              <option value="active">Actif</option>
              <option value="expired">Expiré</option>
              <option value="terminated">Résilié</option>
            </select>
          </div>
        </div>
        <div class="form-row">
          <div class="form-group full-width">
            <label>Notes</label>
            <textarea 
              v-model="contratData.notes" 
              rows="3"
              placeholder="Notes optionnelles sur le contrat"
            ></textarea>
          </div>
        </div>
      </div>

      <div class="form-section">
        <h4>Document du contrat</h4>
        <div class="form-row">
          <div class="form-group">
            <label>Fichier du contrat (PDF, DOC, DOCX)</label>
            <input 
              type="file" 
              @change="handleFileUpload"
              accept=".pdf,.doc,.docx"
            />
            <small class="help-text">Optionnel - Vous pouvez joindre le document du contrat signé</small>
          </div>
        </div>
      </div>

      <div class="form-actions">
        <button type="button" @click="$emit('previous')" class="btn-secondary">
          Retour
        </button>
        <button type="submit" class="btn-primary" :disabled="isCreating">
          {{ isCreating ? 'Création en cours...' : 'Créer le contrat' }}
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import contratService from '@/services/contratService'

export default {
  name: 'ContratStepWizard',
  props: {
    client: {
      type: Object,
      required: true
    },
    bien: {
      type: Object,
      required: true
    }
  },
  emits: ['contract-created', 'previous'],
  data() {
    return {
      isCreating: false,
      contractFile: null,
      contratData: {
        // Backend required fields
        tenant_id: null,
        property_id: null,
        start_date: '',
        end_date: '',
        duration: 12,
        rent_amount: '',
        payment_frequency: 'monthly',
        tax_rate: 20.00,
        discount: 0.00,
        status: 'pending',
        notes: '',
        first_payment_date: ''
      }
    }
  },
  computed: {
    formattedDuration() {
      if (this.contratData.start_date && this.contratData.end_date) {
        const startDate = new Date(this.contratData.start_date)
        const endDate = new Date(this.contratData.end_date)
        
        if (endDate > startDate) {
          const diffTime = Math.abs(endDate - startDate)
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          const diffMonths = Math.round(diffDays / 30.44)
          
          if (diffMonths < 1) {
            return `${diffDays} jour${diffDays > 1 ? 's' : ''}`
          } else if (diffMonths < 12) {
            return `${diffMonths} mois`
          } else {
            const years = Math.floor(diffMonths / 12)
            const remainingMonths = diffMonths % 12
            if (remainingMonths === 0) {
              return `${years} an${years > 1 ? 's' : ''}`
            } else {
              return `${years} an${years > 1 ? 's' : ''} et ${remainingMonths} mois`
            }
          }
        }
      }
      return 'Non calculé'
    }
  },
  mounted() {
    this.initializeContractData()
  },
  methods: {
    initializeContractData() {
      // Set IDs - using the correct backend field names
      this.contratData.tenant_id = this.client.id
      this.contratData.property_id = this.bien.id
      
      // Set default values based on property
      this.contratData.rent_amount = this.bien.price || this.bien.prix_location || ''
      
      // Set default start date to today
      const today = new Date().toISOString().split('T')[0]
      this.contratData.start_date = today
      
      // Set default first payment date to start date
      this.contratData.first_payment_date = today
      
      // Set default end date (1 year from start)
      const defaultEndDate = new Date(today)
      defaultEndDate.setFullYear(defaultEndDate.getFullYear() + 1)
      this.contratData.end_date = defaultEndDate.toISOString().split('T')[0]
      
      this.calculateDuration()
    },

    calculateDuration() {
      if (this.contratData.start_date && this.contratData.end_date) {
        const startDate = new Date(this.contratData.start_date)
        const endDate = new Date(this.contratData.end_date)
        
        if (endDate > startDate) {
          const diffTime = Math.abs(endDate - startDate)
          const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
          const diffMonths = Math.round(diffDays / 30.44) // Average days per month
          
          this.contratData.duration = diffMonths
        } else {
          this.contratData.duration = 1 // Minimum duration
        }
      }
    },

    handleFileUpload(event) {
      const file = event.target.files[0]
      if (file) {
        this.contractFile = file
      }
    },

    async createContract() {
      this.isCreating = true
      try {
        // Prepare contract data - ONLY backend supported fields
        const contractPayload = new FormData()
        
        // Required fields
        contractPayload.append('tenant_id', this.contratData.tenant_id)
        contractPayload.append('property_id', this.contratData.property_id)
        contractPayload.append('start_date', this.contratData.start_date)
        contractPayload.append('end_date', this.contratData.end_date)
        contractPayload.append('duration', parseInt(this.contratData.duration))
        contractPayload.append('rent_amount', parseFloat(this.contratData.rent_amount))
        contractPayload.append('payment_frequency', this.contratData.payment_frequency)
        contractPayload.append('tax_rate', parseFloat(this.contratData.tax_rate))
        contractPayload.append('status', this.contratData.status)
        
        // Optional fields
        if (this.contratData.discount) {
          contractPayload.append('discount', parseFloat(this.contratData.discount))
        }
        if (this.contratData.notes) {
          contractPayload.append('notes', this.contratData.notes)
        }
        if (this.contratData.first_payment_date) {
          contractPayload.append('first_payment_date', this.contratData.first_payment_date)
        }
        if (this.contractFile) {
          contractPayload.append('contract_file', this.contractFile)
        }

        const response = await contratService.createContrat(contractPayload)
        
        if (response.success) {
          this.$emit('contract-created', response.data)
        } else {
          throw new Error(response.message || 'Erreur lors de la création du contrat')
        }
      } catch (error) {
        console.error('Error creating contract:', error)
        alert('Erreur lors de la création du contrat: ' + (error.message || 'Erreur inconnue'))
      } finally {
        this.isCreating = false
      }
    },

    getTypeLabel(type) {
      const types = {
        'apartment': 'Appartement',
        'shop': 'Magasin',
        'office': 'Bureau',
        'house': 'Maison',
        'land': 'Terrain'
      }
      return types[type] || type
    },

    formatPrice(price) {
      if (!price) return ''
      return new Intl.NumberFormat('fr-FR').format(price)
    }
  }
}
</script>

<style scoped>
.contrat-step-wizard {
  height: 100%;
  padding: 32px;
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  position: relative;
}

.contrat-step-wizard::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 20% 80%, rgba(203, 184, 124, 0.05) 0%, transparent 50%);
  pointer-events: none;
}

.step-header {
  text-align: center;
  margin-bottom: 40px;
  background: linear-gradient(135deg, white 0%, #f8fafc 100%);
  padding: 32px;
  border-radius: 16px;
  border: 2px solid rgba(203, 184, 124, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.step-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #22305a, #cbb87c, #22305a);
}

.step-header h3 {
  margin: 0 0 12px 0;
  color: #22305a;
  font-size: 1.8rem;
  font-weight: 700;
  background: linear-gradient(45deg, #22305a, #2c3e50);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.step-header p {
  margin: 0;
  color: #64748b;
  font-size: 1.1rem;
  font-weight: 500;
  letter-spacing: 0.3px;
}

.selection-summary {
  margin-bottom: 40px;
  padding: 32px;
  background: linear-gradient(135deg, white 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 2px solid rgba(203, 184, 124, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.selection-summary::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #10b981, #059669);
}

.summary-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
}

.summary-card {
  background: linear-gradient(135deg, white 0%, #fafbfc 100%);
  padding: 24px;
  border-radius: 12px;
  border: 2px solid rgba(34, 48, 90, 0.1);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.summary-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #22305a, #cbb87c);
}

.summary-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(34, 48, 90, 0.15);
  border-color: #cbb87c;
}

.summary-card h4 {
  margin: 0 0 16px 0;
  color: #22305a;
  font-size: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  padding-left: 8px;
}

.summary-card h4::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 8px;
  width: 30px;
  height: 2px;
  background: linear-gradient(90deg, #cbb87c, #22305a);
  border-radius: 1px;
}

.info {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-left: 8px;
}

.info strong {
  color: #22305a;
  font-size: 1.2rem;
  font-weight: 700;
}

.info span {
  color: #64748b;
  font-size: 0.95rem;
  font-weight: 500;
  padding: 2px 0;
}

.contrat-form {
  max-width: 900px;
  margin: 0 auto;
  position: relative;
}

.form-section {
  margin-bottom: 40px;
  padding: 32px;
  background: linear-gradient(135deg, white 0%, #f8fafc 100%);
  border-radius: 16px;
  border: 2px solid rgba(203, 184, 124, 0.2);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #22305a, #cbb87c, #22305a);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.form-section:hover {
  transform: translateY(-2px);
  border-color: #cbb87c;
  box-shadow: 0 12px 35px rgba(203, 184, 124, 0.2);
}

.form-section:hover::before {
  opacity: 1;
}

.form-section h4 {
  margin: 0 0 24px 0;
  color: #22305a;
  font-size: 1.3rem;
  font-weight: 700;
  border-bottom: none;
  padding: 12px 20px;
  background: linear-gradient(135deg, #22305a, #2c3e50);
  color: white;
  border-radius: 20px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 15px rgba(34, 48, 90, 0.3);
  text-transform: uppercase;
  letter-spacing: 1px;
}

.form-section h4::before {
  content: '●';
  color: #cbb87c;
  font-size: 0.9rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  position: relative;
}

.form-group label {
  margin-bottom: 10px;
  font-weight: 700;
  color: #22305a;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  position: relative;
}

.form-group label::after {
  content: '';
  position: absolute;
  bottom: -4px;
  left: 0;
  width: 25px;
  height: 2px;
  background: linear-gradient(90deg, #cbb87c, #22305a);
  border-radius: 1px;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 14px 18px;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: linear-gradient(135deg, white 0%, #fafbfc 100%);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #cbb87c;
  box-shadow: 0 0 0 4px rgba(203, 184, 124, 0.2), 0 4px 12px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
  background: white;
}

.calculated-field {
  position: relative;
  display: flex;
  align-items: center;
}

.calculated-field input {
  background: #f8f9fa !important;
  color: #495057 !important;
  font-weight: 600 !important;
  border: 2px solid #e9ecef !important;
  cursor: not-allowed !important;
  padding-right: 45px !important;
}

.calc-icon {
  position: absolute;
  right: 16px;
  font-size: 1.1rem;
  color: #6c757d;
  pointer-events: none;
}

.checkbox-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}

.checkbox-item {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 12px;
  border-radius: 6px;
  transition: background 0.2s;
  border: 1px solid #e5e7eb;
}

.checkbox-item:hover {
  background: #f9fafb;
  border-color: #d1d5db;
}

.checkbox-item input[type="checkbox"] {
  width: 16px;
  height: 16px;
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 48px;
  padding: 32px;
  border-top: 1px solid rgba(203, 184, 124, 0.2);
  background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
  border-radius: 16px;
  margin-left: -32px;
  margin-right: -32px;
  margin-bottom: -32px;
  position: relative;
}

.form-actions::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, #cbb87c, transparent);
}

.btn-primary, .btn-secondary {
  padding: 16px 40px;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  font-size: 1rem;
  text-transform: uppercase;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
}

.btn-primary {
  background: linear-gradient(135deg, #22305a, #2c3e50);
  color: white;
  box-shadow: 0 4px 15px rgba(34, 48, 90, 0.3);
}

.btn-primary::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(203, 184, 124, 0.3), transparent);
  transition: left 0.5s;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #1a2647, #243346);
  transform: translateY(-3px);
  box-shadow: 0 8px 25px rgba(34, 48, 90, 0.4);
}

.btn-primary:hover:not(:disabled)::before {
  left: 100%;
}

.btn-primary:disabled {
  background: #9ca3af;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.btn-secondary {
  background: linear-gradient(135deg, #f1f5f9, #e2e8f0);
  color: #475569;
  border: 2px solid #d1d5db;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.btn-secondary:hover {
  background: linear-gradient(135deg, #e2e8f0, #cbd5e1);
  border-color: #22305a;
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

/* Enhanced animations */
.form-section {
  animation: fadeInUp 0.6s ease-out;
}

.form-section:nth-child(1) {
  animation-delay: 0.1s;
}

.form-section:nth-child(2) {
  animation-delay: 0.2s;
}

.form-section:nth-child(3) {
  animation-delay: 0.3s;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .contrat-step-wizard {
    padding: 20px;
  }
  
  .step-header {
    padding: 24px;
  }
  
  .selection-summary {
    padding: 24px;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
  }
  
  .form-section {
    padding: 24px;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .checkbox-grid {
    grid-template-columns: 1fr;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 16px;
    padding: 24px;
  }
  
  .btn-primary, .btn-secondary {
    width: 100%;
  }
}
</style>